import { ResolverTag } from '@ps/web-core-enums';
import type { Currency } from '@ps/web-core-enums';
import { Translation, useResolver } from '@ps/web-shared-components';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import type {
  MaxLimitData,
  MinLimitData,
  PaySystem,
  PaySystemDetails,
  SuggestedDepositAmountsForCurrency
} from '../../../../../../types/Cashier';
import { CardType } from '../../../../../../types/cardType';
import { getConversionRate, getPaySystemName } from '../../../../../../utils/cashier';
import { createMaxLimitValidator } from '../../../../../../validators/maxLimit';
import { createMinLimitValidator } from '../../../../../../validators/minLimit';
import { createStaticMessage } from '../../../../../../validators/tempAccountMessage';
import AmountCurrency from '../../../../../AmountCurrency/AmountCurrency';
import ConversionInformation from '../../../../../ConversionInformation/ConversionInformation';
import AmountSuggestions from '../AmountSuggestions/AmountSuggestions';
import VoucherSuggestions from '../VoucherSuggestions/VoucherSuggestions';

import {
  TXTCLI_Amount,
  TXTWCL_Please_confirm_your_deposit_to_ELL,
  TXTWCL_View_current_exchange_rates,
  TXTWCL_XbXPlease_noteXX_bX_Currency_e_ELL
} from './AmountContainer.messages.json';
import { message } from './AmountContainer.scss';

const fieldName = 'deposit';

const AmountContainer: React.FC = () => {
  const countryCurrency = useSelector(select.account.getDefaultCurrencyForCountry) as Currency;
  const realMoneyPspayAstropayRate = useResolver(ResolverTag.RealMoneyPspayAstropayRate);
  const isRomanianTemporaryAccount = useSelector(select.account.isRomanianTemporaryAccount);

  const {
    values: {
      deposit: { bankInfo }
    },
    data: { paySystemDetails, paySystems, voucherInfo }
  } = useWizardContext();

  const {
    input: {
      value: { currency }
    }
  } = useField(fieldName, { subscription: { value: true } });

  const {
    input: { onChange: onDepositAmountChange, value: amount }
  } = useField('deposit.amount', {
    subscription: { value: true }
  });

  if (!paySystemDetails) {
    return null;
  }

  const {
    rates: { curr: balanceCurrency, rates },
    suggestedDepositAmounts: { suggestedDepositAmountsForCurrency },
    depositLimitHitType,
    daysLimitHitType,
    remainingLimit
  }: PaySystemDetails = paySystemDetails;

  if (!rates.length) {
    return null;
  }

  const currencies = !bankInfo ? rates.map(({ curr }) => curr).sort() : [bankInfo.currency];
  const defaultCurrencies = !bankInfo ? [countryCurrency, balanceCurrency] : [bankInfo.currency];
  const defaultCurrency =
    defaultCurrencies.find((curr) => currencies.includes(curr)) || currencies[0];
  const selectedCurrency: Currency = currency || defaultCurrency;
  const amountSuggestions = suggestedDepositAmountsForCurrency.find(
    (suggestion) => suggestion.currency === selectedCurrency
  ) as SuggestedDepositAmountsForCurrency;

  const { type } = paySystemDetails;
  const systemName = getPaySystemName(paySystems, type);
  const hideConversion = [
    CardType.BoletoAstropay,
    CardType.PsPayAstropay,
    CardType.PsPayInovapay
  ].includes(type);

  const maxLimit: MaxLimitData = paySystems.reduce(
    (result: MaxLimitData, { cardType, transaction, day, week, month }: PaySystem) =>
      cardType === type ? { transaction, day, week, month } : result,
    { transaction: 1, day: 1, week: 1, month: 1 }
  );

  const minLimit: MinLimitData = paySystems.reduce(
    (result: MinLimitData, { cardType, once, transaction }: PaySystem) =>
      cardType === type ? { once, transaction } : result,
    { once: 1, transaction: 1 }
  );
  const lowestMaxLimit =
    remainingLimit / 100 < maxLimit.transaction ? remainingLimit / 100 : maxLimit.transaction;

  minLimit.transaction = lowestMaxLimit;

  const maxLimitValidator = createMaxLimitValidator(
    balanceCurrency,
    daysLimitHitType,
    depositLimitHitType,
    maxLimit,
    rates,
    remainingLimit
  );
  const minLimitValidator = createMinLimitValidator(
    minLimit.once,
    minLimit.transaction,
    rates,
    balanceCurrency
  );
  const conversionRate = getConversionRate(rates, currency);

  const staticMessage = isRomanianTemporaryAccount
    ? createStaticMessage(selectedCurrency, depositLimitHitType, rates, remainingLimit)
    : undefined;

  return !voucherInfo ? (
    <>
      <AmountCurrency
        amountFieldName="deposit.amount"
        currencyFieldName="deposit.currency"
        defaultCurrency={defaultCurrency}
        includeCurrencies={currencies}
        onAmountBlur={() => onDepositAmountChange(Number(amount).toFixed(2))}
        validators={[minLimitValidator, maxLimitValidator]}
        label={TXTCLI_Amount}
        staticMessage={staticMessage}
        extension={
          <AmountSuggestions suggestions={amountSuggestions?.buttons} currency={selectedCurrency} />
        }
      />
      {selectedCurrency !== balanceCurrency && !hideConversion && (
        <ConversionInformation
          rate={conversionRate}
          balanceCurrency={balanceCurrency}
          fieldName={fieldName}
          rates={rates}
        />
      )}
      {hideConversion && (
        <>
          <div className={message}>
            <Translation
              {...TXTWCL_XbXPlease_noteXX_bX_Currency_e_ELL}
              values={{ 0: systemName }}
            />
          </div>
          {type === CardType.BoletoAstropay && (
            <a href={realMoneyPspayAstropayRate} target="_blank" rel="noopener noreferrer">
              <Translation {...TXTWCL_View_current_exchange_rates} />
            </a>
          )}
          <div className={message}>
            <Translation {...TXTWCL_Please_confirm_your_deposit_to_ELL} />
          </div>
        </>
      )}
    </>
  ) : (
    <VoucherSuggestions suggestions={voucherInfo.voucherAmounts} />
  );
};

export default AmountContainer;
